/**
 * Sistema de Cache Persistente de Mídias - Netflix-style Offline Storage
 * 
 * Utiliza IndexedDB para armazenamento persistente de mídias (imagens, vídeos)
 * Funcionalidades:
 * - Cache persistente que sobrevive ao fechamento do navegador
 * - Verificação de hash/versão para detectar alterações
 * - Suporte a grandes arquivos de vídeo
 * - Limpeza automática de cache antigo
 * - Estatísticas de uso
 */

const DB_NAME = 'watch-media-cache-v2';
const STORE_NAME = 'medias';
const METADATA_STORE = 'metadata';
const DB_VERSION = 2;

// Estrutura dos dados no cache
export interface CachedMedia {
  id: string;
  blob: Blob;
  url: string;           // URL original para verificação
  hash: string;          // Hash para detectar alterações
  mimeType: string;      // Tipo MIME do arquivo
  size: number;          // Tamanho em bytes
  title?: string;        // Título da mídia
  cachedAt: number;      // Timestamp de quando foi cacheado
  lastAccessed: number;  // Último acesso
  accessCount: number;   // Contador de acessos
  format?: string;       // Formato da mídia (para analytics)
}

export interface CacheMetadata {
  id: string;
  totalSize: number;
  mediaCount: number;
  lastCleanup: number;
  version: string;
}

// Limite de cache (500MB por padrão)
const MAX_CACHE_SIZE_BYTES = 500 * 1024 * 1024;

// Tempo máximo para manter mídia não acessada (7 dias)
const MAX_UNUSED_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Abre (ou cria) o banco de dados IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;

      // Store principal para mídias
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('url', 'url', { unique: false });
        store.createIndex('hash', 'hash', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
        store.createIndex('lastAccessed', 'lastAccessed', { unique: false });
      }

      // Store de metadados globais
      if (!db.objectStoreNames.contains(METADATA_STORE)) {
        db.createObjectStore(METADATA_STORE, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Gera um hash simples para uma URL (para detectar alterações)
 * Em produção, poderia usar ETag ou Last-Modified do servidor
 */
export function generateUrlHash(url: string, size?: number): string {
  // Combina URL + timestamp de query params (se houver) + tamanho
  const urlObj = new URL(url);
  const key = `${urlObj.pathname}${urlObj.search}${size || ''}`;

  // Hash simples baseado no algoritmo djb2
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) + hash) + key.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Obtém o tipo MIME de um blob ou infere da URL
 */
function getMimeType(blob: Blob, url: string): string {
  if (blob.type && blob.type !== 'application/octet-stream') {
    return blob.type;
  }

  // Inferir do URL
  const extension = url.toLowerCase().split('.').pop()?.split('?')[0] || '';
  const mimeMap: Record<string, string> = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'ogg': 'video/ogg',
    'mov': 'video/quicktime',
  };

  return mimeMap[extension] || blob.type || 'application/octet-stream';
}

/**
 * Verifica se uma mídia está no cache e é válida
 */
export async function isMediaCached(id: string, url?: string): Promise<boolean> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);

      req.onsuccess = () => {
        const cached = req.result as CachedMedia | undefined;
        if (!cached) {
          resolve(false);
          return;
        }

        // Se URL foi fornecida, verificar se é a mesma
        if (url && cached.url !== url) {
          console.log(`🔄 [CACHE] URL diferente detectada para ${id}, requerendo re-download`);
          resolve(false);
          return;
        }

        resolve(true);
      };

      req.onerror = () => resolve(false);
    });
  } catch (error) {
    console.warn('⚠️ [CACHE] Erro ao verificar cache:', error);
    return false;
  }
}

/**
 * Busca uma mídia do cache
 */
export async function getMediaFromCache(id: string): Promise<Blob | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);

      req.onsuccess = () => {
        const cached = req.result as CachedMedia | undefined;
        if (cached && cached.blob) {
          // Atualizar contador de acesso
          cached.lastAccessed = Date.now();
          cached.accessCount = (cached.accessCount || 0) + 1;
          store.put(cached);

          console.log(`🎯 [CACHE] Hit para: ${cached.title || id} (${(cached.size / 1024).toFixed(1)}KB)`);
          resolve(cached.blob);
        } else {
          resolve(null);
        }
      };

      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn('⚠️ [CACHE] Erro ao buscar do cache:', error);
    return null;
  }
}

/**
 * Busca informações completas de uma mídia cacheada
 */
export async function getCachedMediaInfo(id: string): Promise<CachedMedia | null> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);

      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (error) {
    return null;
  }
}

/**
 * Salva uma mídia no cache
 */
export async function saveMediaToCache(
  id: string,
  blob: Blob,
  url: string,
  title?: string
): Promise<void> {
  try {
    const db = await openDB();
    const now = Date.now();
    const mimeType = getMimeType(blob, url);
    const hash = generateUrlHash(url, blob.size);

    const cachedMedia: CachedMedia = {
      id,
      blob,
      url,
      hash,
      mimeType,
      size: blob.size,
      title,
      cachedAt: now,
      lastAccessed: now,
      accessCount: 1,
      format: mimeType.split('/')[1] || 'unknown'
    };

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      store.put(cachedMedia);

      tx.oncomplete = () => {
        console.log(`✅ [CACHE] Salvo: ${title || id} (${(blob.size / 1024 / 1024).toFixed(2)}MB)`);
        resolve();
      };

      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('❌ [CACHE] Erro ao salvar:', error);
    throw error;
  }
}

/**
 * Remove uma mídia do cache
 */
export async function deleteMediaFromCache(id: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(id);
      tx.oncomplete = () => {
        console.log(`🗑️ [CACHE] Removido: ${id}`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.warn('⚠️ [CACHE] Erro ao deletar:', error);
  }
}

/**
 * Busca todas as mídias em cache
 */
export async function getAllMediasFromCache(): Promise<CachedMedia[]> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();

      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn('⚠️ [CACHE] Erro ao listar cache:', error);
    return [];
  }
}

/**
 * Limpa todo o cache
 */
export async function clearMediaCache(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();
      tx.oncomplete = () => {
        console.log('🧹 [CACHE] Cache completamente limpo');
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('❌ [CACHE] Erro ao limpar cache:', error);
    throw error;
  }
}

/**
 * Calcula estatísticas do cache
 */
export async function getCacheStats(): Promise<{
  totalSize: number;
  totalSizeMB: string;
  mediaCount: number;
  oldestCacheDate: Date | null;
  newestCacheDate: Date | null;
  averageAccessCount: number;
}> {
  try {
    const allMedias = await getAllMediasFromCache();

    const totalSize = allMedias.reduce((sum, m) => sum + (m.size || 0), 0);
    const accessCounts = allMedias.map(m => m.accessCount || 0);
    const avgAccess = accessCounts.length > 0
      ? accessCounts.reduce((a, b) => a + b, 0) / accessCounts.length
      : 0;

    const cachedDates = allMedias.map(m => m.cachedAt).filter(Boolean);

    return {
      totalSize,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
      mediaCount: allMedias.length,
      oldestCacheDate: cachedDates.length > 0 ? new Date(Math.min(...cachedDates)) : null,
      newestCacheDate: cachedDates.length > 0 ? new Date(Math.max(...cachedDates)) : null,
      averageAccessCount: Math.round(avgAccess * 10) / 10
    };
  } catch (error) {
    return {
      totalSize: 0,
      totalSizeMB: '0',
      mediaCount: 0,
      oldestCacheDate: null,
      newestCacheDate: null,
      averageAccessCount: 0
    };
  }
}

/**
 * Limpa mídias antigas ou não utilizadas para liberar espaço
 */
export async function cleanupOldCache(activeMediaIds: string[]): Promise<number> {
  try {
    const allMedias = await getAllMediasFromCache();
    const now = Date.now();
    let removedCount = 0;
    let freedBytes = 0;

    for (const media of allMedias) {
      let shouldRemove = false;

      // 1. Remover se não está mais na playlist ativa
      if (!activeMediaIds.includes(media.id)) {
        console.log(`🧹 [CACHE] Removendo mídia não ativa: ${media.title || media.id}`);
        shouldRemove = true;
      }

      // 2. Remover se não foi acessada há muito tempo
      else if (media.lastAccessed && (now - media.lastAccessed) > MAX_UNUSED_AGE_MS) {
        console.log(`🧹 [CACHE] Removendo mídia não acessada há 7+ dias: ${media.title || media.id}`);
        shouldRemove = true;
      }

      if (shouldRemove) {
        await deleteMediaFromCache(media.id);
        removedCount++;
        freedBytes += media.size || 0;
      }
    }

    if (removedCount > 0) {
      console.log(`🧹 [CACHE] Limpeza concluída: ${removedCount} mídias removidas, ${(freedBytes / 1024 / 1024).toFixed(2)}MB liberados`);
    }

    return removedCount;
  } catch (error) {
    console.warn('⚠️ [CACHE] Erro na limpeza:', error);
    return 0;
  }
}

/**
 * Verifica se há espaço suficiente para cache
 */
export async function hasCacheSpace(requiredBytes: number): Promise<boolean> {
  try {
    const stats = await getCacheStats();
    return (stats.totalSize + requiredBytes) < MAX_CACHE_SIZE_BYTES;
  } catch {
    return true; // Assume que tem espaço em caso de erro
  }
}

/**
 * Força limpeza de espaço removendo mídias menos acessadas
 */
export async function forceFreeSpace(requiredBytes: number): Promise<boolean> {
  try {
    const allMedias = await getAllMediasFromCache();

    // Ordena por último acesso (mais antigo primeiro)
    allMedias.sort((a, b) => (a.lastAccessed || 0) - (b.lastAccessed || 0));

    let freedBytes = 0;
    for (const media of allMedias) {
      if (freedBytes >= requiredBytes) break;

      await deleteMediaFromCache(media.id);
      freedBytes += media.size || 0;
      console.log(`🧹 [CACHE] Liberando espaço: removido ${media.title || media.id}`);
    }

    return freedBytes >= requiredBytes;
  } catch (error) {
    console.error('❌ [CACHE] Erro ao liberar espaço:', error);
    return false;
  }
}

/**
 * Verifica se o navegador suporta IndexedDB
 */
export function isCacheSupported(): boolean {
  try {
    return 'indexedDB' in window && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Estima o espaço disponível para cache (usando Storage API se disponível)
 */
export async function getAvailableSpace(): Promise<{
  quota: number;
  usage: number;
  available: number;
  percentUsed: number;
} | null> {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const quota = estimate.quota || 0;
      const usage = estimate.usage || 0;
      const available = quota - usage;

      return {
        quota,
        usage,
        available,
        percentUsed: quota > 0 ? Math.round((usage / quota) * 100) : 0
      };
    }
    return null;
  } catch {
    return null;
  }
}

// =============================================================================
// ARMAZENAMENTO PERSISTENTE - Evita que o navegador apague dados automaticamente
// Similar ao sistema do Netflix para downloads offline
// =============================================================================

/**
 * Status do armazenamento persistente
 */
export interface PersistentStorageStatus {
  isSupported: boolean;     // Se o navegador suporta persistent storage
  isPersisted: boolean;     // Se o armazenamento está persistente
  wasRequested: boolean;    // Se já foi solicitado
  quota?: number;           // Quota total em bytes
  usage?: number;           // Uso atual em bytes
  available?: number;       // Espaço disponível
  percentUsed?: number;     // Percentual usado
}

/**
 * Verifica se o armazenamento persistente é suportado
 */
export function isPersistentStorageSupported(): boolean {
  return 'storage' in navigator && 'persist' in navigator.storage;
}

/**
 * Verifica se o armazenamento já está persistente
 */
export async function isStoragePersisted(): Promise<boolean> {
  try {
    if (isPersistentStorageSupported()) {
      return await navigator.storage.persisted();
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Solicita armazenamento persistente ao navegador
 * Isso evita que o navegador apague dados automaticamente quando precisar de espaço
 * 
 * IMPORTANTE: Em alguns navegadores (Chrome), isso é concedido automaticamente
 * se o site for adicionado à tela inicial ou tiver bom engajamento.
 * Em outros (Firefox), mostra um prompt ao usuário.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!isPersistentStorageSupported()) {
      console.warn('⚠️ [PERSISTENT STORAGE] Navegador não suporta armazenamento persistente');
      return false;
    }

    // Verificar se já está persistente
    const alreadyPersisted = await navigator.storage.persisted();
    if (alreadyPersisted) {
      console.log('✅ [PERSISTENT STORAGE] Armazenamento já está persistente');
      return true;
    }

    // Solicitar permissão de armazenamento persistente
    const granted = await navigator.storage.persist();

    if (granted) {
      console.log('✅ [PERSISTENT STORAGE] Armazenamento persistente CONCEDIDO! O navegador não vai apagar os dados automaticamente.');
    } else {
      console.warn('⚠️ [PERSISTENT STORAGE] Armazenamento persistente NEGADO. O navegador pode limpar dados quando precisar de espaço.');
    }

    return granted;
  } catch (error) {
    console.error('❌ [PERSISTENT STORAGE] Erro ao solicitar armazenamento persistente:', error);
    return false;
  }
}

/**
 * Obtém status completo do armazenamento persistente
 */
export async function getPersistentStorageStatus(): Promise<PersistentStorageStatus> {
  const isSupported = isPersistentStorageSupported();

  if (!isSupported) {
    return {
      isSupported: false,
      isPersisted: false,
      wasRequested: false
    };
  }

  try {
    const isPersisted = await navigator.storage.persisted();
    const estimate = await navigator.storage.estimate();

    return {
      isSupported: true,
      isPersisted,
      wasRequested: true,
      quota: estimate.quota,
      usage: estimate.usage,
      available: (estimate.quota || 0) - (estimate.usage || 0),
      percentUsed: estimate.quota ? Math.round(((estimate.usage || 0) / estimate.quota) * 100) : 0
    };
  } catch {
    return {
      isSupported: true,
      isPersisted: false,
      wasRequested: false
    };
  }
}

/**
 * Inicializa o sistema de cache persistente
 * - Solicita permissão de armazenamento persistente
 * - Abre/cria o banco de dados IndexedDB
 * - Retorna o status da inicialização
 */
export async function initializePersistentCache(): Promise<{
  success: boolean;
  isPersisted: boolean;
  dbReady: boolean;
  storageInfo: PersistentStorageStatus;
  error?: string;
}> {
  console.log('🚀 [CACHE] Inicializando sistema de cache persistente Netflix-style...');

  try {
    // 1. Solicitar armazenamento persistente
    const persistGranted = await requestPersistentStorage();

    // 2. Verificar suporte ao IndexedDB
    if (!isCacheSupported()) {
      return {
        success: false,
        isPersisted: persistGranted,
        dbReady: false,
        storageInfo: await getPersistentStorageStatus(),
        error: 'IndexedDB não suportado neste navegador'
      };
    }

    // 3. Abrir/criar o banco de dados
    try {
      const db = await openDB();
      db.close(); // Fechar conexão de teste
    } catch (dbError) {
      return {
        success: false,
        isPersisted: persistGranted,
        dbReady: false,
        storageInfo: await getPersistentStorageStatus(),
        error: `Erro ao abrir IndexedDB: ${dbError}`
      };
    }

    // 4. Obter status do armazenamento
    const storageInfo = await getPersistentStorageStatus();

    console.log('✅ [CACHE] Sistema de cache persistente inicializado:', {
      persistente: storageInfo.isPersisted,
      quotaTotal: `${((storageInfo.quota || 0) / 1024 / 1024 / 1024).toFixed(2)}GB`,
      usoAtual: `${((storageInfo.usage || 0) / 1024 / 1024).toFixed(2)}MB`,
      disponivel: `${((storageInfo.available || 0) / 1024 / 1024 / 1024).toFixed(2)}GB`
    });

    return {
      success: true,
      isPersisted: storageInfo.isPersisted,
      dbReady: true,
      storageInfo
    };

  } catch (error) {
    console.error('❌ [CACHE] Erro na inicialização do cache:', error);
    return {
      success: false,
      isPersisted: false,
      dbReady: false,
      storageInfo: {
        isSupported: false,
        isPersisted: false,
        wasRequested: false
      },
      error: error instanceof Error ? error.message : 'Erro desconhecido'
    };
  }
}

/**
 * Formata bytes para exibição legível
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Exportações para compatibilidade com código existente
export { openDB };

